import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import RozliczeniaService from '@/services/RozliczeniaService'

export default function useRozliczeniaList() {
	const { t } = useTranslation()
	const defaultLimit = 50
	const limitIncrement = 50

	const [data, setData] = useState([])
	const [loading, setLoading] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [error, setError] = useState(null)
	const [totalCount, setTotalCount] = useState(0)
	const [hasMore, setHasMore] = useState(false)
	const [limit, setLimit] = useState(defaultLimit)
	const [searchQuery, setSearchQuery] = useState('')
	const [sortColumn, setSortColumn] = useState('nazwa')
	const [sortDirection, setSortDirection] = useState('asc')
	const loadingMoreRef = useRef(false)

	const loadData = useCallback(async () => {
		if (!loadingMoreRef.current) setLoading(true)
		setError(null)

		const result = await RozliczeniaService.getTrainers({
			limit,
			sortColumn,
			sortDirection,
			filters: { searchText: searchQuery },
		})

		if (result.error) {
			setError(result.error)
			setData([])
			setTotalCount(0)
			setHasMore(false)
		} else {
			setData(Array.isArray(result.data) ? result.data : [])
			setTotalCount(result.totalCount || 0)
			setHasMore(!!result.hasMore)
		}

		loadingMoreRef.current = false
		setLoading(false)
		setLoadingMore(false)
	}, [limit, searchQuery, sortColumn, sortDirection])

	useEffect(() => {
		loadData()
	}, [loadData])

	useEffect(() => {
		if (!error) return
		Swal.fire({
			title: t('notifications.errorTitle'),
			text: error,
			icon: 'error',
		}).then(() => setError(null))
	}, [error, t])

	const handleLoadMore = useCallback(() => {
		if (hasMore && !loadingMoreRef.current) {
			loadingMoreRef.current = true
			setLimit(prev => prev + limitIncrement)
			setLoadingMore(true)
		}
	}, [hasMore])

	const handleSort = useCallback(column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSortColumn(column)
		setSortDirection(newDirection)
	}, [sortColumn, sortDirection])

	const handleSearchChange = useCallback(event => {
		setSearchQuery(event.target.value)
		setLimit(defaultLimit)
	}, [])

	return {
		data,
		loading,
		loadingMore,
		totalCount,
		hasMore,
		searchQuery,
		sortColumn,
		sortDirection,
		handleLoadMore,
		handleSort,
		handleSearchChange,
	}
}
